import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { ResidentDetailsModalProps } from './types';
import { resolveEventAttendanceFlags } from '@/utils/admin/eventsAttendance';

export const ResidentDetailsModal: React.FC<ResidentDetailsModalProps> = ({ 
  visible, 
  resident, 
  onClose, 
  onCheckIn,
  eventId,
}) => {
  if (!resident) return null;

  const { checkedIn: isCheckedIn, hasRsvp } = resolveEventAttendanceFlags(resident, eventId);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.detailsModalContainer}>
        <View style={styles.detailsModalHeader}>
          <Text style={styles.detailsModalTitle}>Resident Details</Text>
          <TouchableOpacity 
            style={styles.detailsModalCloseButton} 
            onPress={onClose}
            accessibilityLabel="Close resident details"
            accessibilityHint="Tap to close the resident details modal"
            accessibilityRole="button"
          >
            <Text style={styles.detailsModalCloseText}>✕</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.detailsModalContent}>
          {/* Resident Info */}
          <View style={styles.residentInfo}>
            <View style={styles.residentAvatar}>
              <Text style={styles.residentAvatarText}>
                {getInitials(resident.firstName, resident.lastName)}
              </Text>
            </View>
            <View style={styles.residentDetails}>
              <Text style={styles.residentName}>
                {resident.firstName} {resident.lastName}
              </Text>
              <Text style={styles.residentEmail}>{resident.email}</Text>
              {resident.unitNumberSpace && (
                <Text style={styles.residentUnit}>Unit {resident.unitNumberSpace}</Text>
              )}
              <View style={styles.residentStatus}>
                <Text style={styles.residentStatusText}>
                  {resident.statusType?.charAt(0).toUpperCase() + resident.statusType?.slice(1)} Resident
                </Text>
              </View>
            </View>
          </View>

          {/* Check-in Status */}
          <View style={styles.checkInSection}>
            <Text style={styles.checkInSectionTitle}>Check-in Status</Text>
            <View style={styles.checkInStatusContainer}>
              {isCheckedIn ? (
                <View style={styles.checkedInStatusLarge}>
                  <View style={styles.checkedInDotLarge} />
                  <Text style={styles.checkedInTextLarge}>Checked In</Text>
                </View>
              ) : (
                <View style={styles.notCheckedInStatusLarge}>
                  <View style={styles.notCheckedInDotLarge} />
                  <Text style={styles.notCheckedInTextLarge}>Not Checked In</Text>
                </View>
              )}
            </View>

            {/* RSVP Status */}
            {hasRsvp && (
              <View style={styles.rsvpStatusContainer}>
                <Text style={styles.rsvpStatusText}>✓ RSVP'd to this event</Text>
              </View>
            )}

            {/* Check-in Actions */}
            <View style={styles.checkInActions}>
              {!isCheckedIn ? (
                <TouchableOpacity
                  style={styles.checkInButton}
                  onPress={() => onCheckIn(resident, true)}
                  accessibilityLabel={`Check in ${resident.firstName} ${resident.lastName}`}
                  accessibilityHint="Tap to check in this resident to the event"
                  accessibilityRole="button"
                >
                  <Text style={styles.checkInButtonText}>Check In</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.checkOutButton}
                  onPress={() => onCheckIn(resident, false)}
                  accessibilityLabel={`Check out ${resident.firstName} ${resident.lastName}`}
                  accessibilityHint="Tap to check out this resident from the event"
                  accessibilityRole="button"
                >
                  <Text style={styles.checkOutButtonText}>Check Out</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  detailsModalContainer: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  detailsModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  detailsModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  detailsModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  detailsModalCloseText: {
    fontSize: 18,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  detailsModalContent: {
    flex: 1,
    padding: 20,
  },
  residentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 32,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER + '40',
  },
  residentAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  residentAvatarText: {
    fontSize: 24,
    fontWeight: '700',
    color: ADMIN_COLORS.PRIMARY,
  },
  residentDetails: {
    flex: 1,
  },
  residentName: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  residentEmail: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 4,
  },
  residentUnit: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 8,
  },
  residentStatus: {
    alignSelf: 'flex-start',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  residentStatusText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  checkInSection: {
    flex: 1,
  },
  checkInSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
  },
  checkInStatusContainer: {
    marginBottom: 16,
  },
  checkedInStatusLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.SUCCESS + '15',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.SUCCESS + '30',
  },
  checkedInDotLarge: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.SUCCESS,
    marginRight: 12,
  },
  checkedInTextLarge: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.SUCCESS,
  },
  notCheckedInStatusLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY + '15',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.TEXT_TERTIARY + '30',
  },
  notCheckedInDotLarge: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY,
    marginRight: 12,
  },
  notCheckedInTextLarge: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  rsvpStatusContainer: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '15',
    padding: 12,
    borderRadius: 8,
    marginBottom: 24,
  },
  rsvpStatusText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
    textAlign: 'center',
  },
  checkInActions: {
    marginTop: 'auto',
    paddingTop: 24,
  },
  checkInButton: {
    backgroundColor: ADMIN_COLORS.SUCCESS,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  checkInButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  checkOutButton: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  checkOutButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
});
